import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Activity, LineChart as LineIcon } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { vitalAlerts } from "./vitalsAlerts";

const WINDOWS = [
  { key: "24h", label: "24 h", hours: 24 },
  { key: "72h", label: "72 h", hours: 72 },
  { key: "7d",  label: "7 d",  hours: 24 * 7 },
];

function formatShortTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function formatTick(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit" });
  } catch { return iso; }
}

function Metric({ label, value, unit, flag }) {
  if (value === undefined || value === null || value === "") return null;
  const flagCls = flag === "high" ? "text-[#B71C1C]" : flag === "low" ? "text-[#B45309]" : "text-slate-800";
  return (
    <div className="flex flex-col items-start min-w-[60px]">
      <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
      <span className={`text-sm font-semibold tabular ${flagCls}`}>{value}{unit ? <span className="text-[10px] text-slate-500 ml-0.5 font-normal">{unit}</span> : null}{flag ? <span className="text-[9px] ml-1 uppercase">{flag}</span> : null}</span>
    </div>
  );
}

export function VitalsTimeline({ patientId }) {
  const [vitals, setVitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [windowKey, setWindowKey] = useState("24h");
  const [mode, setMode] = useState("chart"); // "chart" | "table"

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/vitals?patient_id=${encodeURIComponent(patientId)}`);
        if (mounted) setVitals(data || []);
      } catch { if (mounted) setVitals([]); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [patientId]);

  const win = WINDOWS.find((w) => w.key === windowKey) || WINDOWS[0];
  const cutoff = Date.now() - win.hours * 60 * 60 * 1000;

  const filtered = useMemo(() => {
    return vitals
      .filter((v) => {
        const t = v.created_at ? new Date(v.created_at).getTime() : 0;
        return t >= cutoff;
      })
      .slice()
      .reverse();
  }, [vitals, cutoff]);

  const chartData = filtered.map((v) => ({
    at: v.created_at,
    "BP (sys)": v.systolic ?? null,
    "Pulse": v.pulse ?? null,
    "SpO₂": v.spo2 ?? null,
  }));

  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="vitals-timeline">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#1976D2]" />
          <div className="text-sm font-semibold text-slate-800">Vitals — last {win.label}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="vitals-window-switch">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                data-testid={`vitals-window-${w.key}`}
                type="button"
                onClick={() => setWindowKey(w.key)}
                className={`h-7 px-2.5 rounded-md text-[11px] font-medium transition ${windowKey === w.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >{w.label}</button>
            ))}
          </div>
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="vitals-mode-switch">
            <button data-testid="vitals-mode-chart" onClick={() => setMode("chart")} className={`h-7 px-2.5 rounded-md text-[11px] font-medium transition ${mode === "chart" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Chart</button>
            <button data-testid="vitals-mode-table" onClick={() => setMode("table")} className={`h-7 px-2.5 rounded-md text-[11px] font-medium transition ${mode === "table" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>Table</button>
          </div>
          <span className="text-[11px] text-slate-500">{filtered.length} record{filtered.length === 1 ? "" : "s"}</span>
        </div>
      </div>

      {loading ? (
        <div className="text-xs text-slate-500" data-testid="vitals-loading">Loading vitals…</div>
      ) : filtered.length === 0 ? (
        <div className="text-xs text-slate-500" data-testid="vitals-empty">No vitals recorded in this window.</div>
      ) : mode === "chart" ? (
        <div className="h-56" data-testid="vitals-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="at" tickFormatter={formatTick} tick={{ fontSize: 10, fill: "#64748b" }} minTickGap={30} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip labelFormatter={formatShortTime} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="BP (sys)" stroke="#B71C1C" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line type="monotone" dataKey="Pulse" stroke="#1976D2" strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line type="monotone" dataKey="SpO₂" stroke="#1B5E20" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 max-h-56 overflow-y-auto" data-testid="vitals-list">
          {[...filtered].reverse().map((v) => {
            const bp = (v.systolic || v.diastolic) ? `${v.systolic ?? "—"}/${v.diastolic ?? "—"}` : null;
            const alerts = vitalAlerts(v);
            return (
              <li key={v.vital_id || v.created_at} className={`py-2.5 flex items-center gap-5 flex-wrap ${alerts.hasAlert ? "bg-[#fdecec]/40 -mx-4 px-4" : ""}`} data-testid={`vital-row-${v.vital_id || v.created_at}`}>
                <div className="min-w-[130px]">
                  <div className="text-xs font-medium text-slate-700 tabular flex items-center gap-1.5">
                    {formatShortTime(v.created_at)}
                    {alerts.hasAlert && <span className="text-[9px] px-1 py-0.5 rounded bg-[#fdecec] text-[#B71C1C] font-semibold uppercase" data-testid={`vitals-alert-${v.vital_id || v.created_at}`}>Alert</span>}
                  </div>
                  <div className="text-[10px] text-slate-500">{v.recorded_by_name || "—"}</div>
                </div>
                <Metric label="BP" value={bp} unit="mmHg" flag={alerts.flags.systolic || alerts.flags.diastolic} />
                <Metric label="Pulse" value={v.pulse} unit="bpm" flag={alerts.flags.pulse} />
                <Metric label="Temp" value={v.temperature != null ? Number(v.temperature).toFixed(1) : null} unit="°C" flag={alerts.flags.temperature} />
                <Metric label="RR" value={v.respiratory_rate} unit="/min" flag={alerts.flags.respiratory_rate} />
                <Metric label="SpO₂" value={v.spo2} unit="%" flag={alerts.flags.spo2} />
                <Metric label="Glu" value={v.glucose} unit="mmol/L" flag={alerts.flags.glucose} />
                <Metric label="Pain" value={v.pain_score} unit="/10" />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
